"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  CARD,
  HINT,
  LABEL,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { SubstitutionStrings } from "@/components/tools/substitution-cipher";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { ALPHABET, letterIndex } from "@/lib/tool-kit/ciphers/alphabet";
import {
  applyWord,
  doubledLetters,
  EMPTY_MAPPING,
  ENGLISH_BY_FREQUENCY,
  englishShare,
  frequencyGuess,
  letterCounts,
  type Mapping,
  mappingConflicts,
  partialDecryption,
  rankLetters,
  setGuess,
  shortWordHints,
  solveChars,
} from "@/lib/tool-kit/ciphers/substitution";

/** The cryptogram-solving half of the substitution cipher tool. */

const HINT_LIMIT = 10;
const CANDIDATE_LIMIT = 8;
const DOUBLE_LIMIT = 8;
const VIEW_LIMIT = 1500;
const WORD_SPLIT = /(\s+)/;

function FrequencyChart({
  counts,
  strings,
}: {
  counts: readonly number[];
  strings: SubstitutionStrings;
}) {
  const fmt = useToolNumber();
  const percent = (share: number) =>
    fmt(share, { style: "percent", maximumFractionDigits: 1 });
  const total = counts.reduce((sum, count) => sum + count, 0);
  const ranked = rankLetters(counts);
  const rows = ENGLISH_BY_FREQUENCY.map((english, rank) => {
    const cipher = ranked[rank];
    const count = counts[letterIndex(cipher)];
    return {
      english,
      cipher: count > 0 ? cipher : null,
      count,
      share: total > 0 ? count / total : 0,
      englishShare: englishShare(english),
    };
  });
  const max = Math.max(
    ...rows.map((row) => Math.max(row.share, row.englishShare))
  );
  const height = (share: number) => `${max > 0 ? (share / max) * 100 : 0}%`;
  return (
    <div className="space-y-3">
      <div aria-hidden="true" className="flex gap-0.5 overflow-x-auto pb-1">
        {rows.map((row) => (
          <div
            className="flex min-w-6 flex-1 flex-col items-center gap-1"
            key={row.english}
          >
            <div className="flex h-28 items-end gap-px">
              <span
                className="w-2 rounded-t bg-sky-500"
                style={{ height: height(row.share) }}
              />
              <span
                className="w-2 rounded-t bg-amber-500"
                style={{ height: height(row.englishShare) }}
              />
            </div>
            <span className="font-mono font-semibold text-foreground text-xs">
              {row.cipher ?? "·"}
            </span>
            <span className="font-mono text-muted-foreground text-xs">
              {row.english.toLowerCase()}
            </span>
          </div>
        ))}
      </div>
      <p className={HINT}>{strings.frequencyHint}</p>
      <details>
        <summary className={`${LABEL} cursor-pointer`}>
          {strings.showTable}
        </summary>
        <div className="mt-3">
          <DataTable
            head={[
              strings.rankColumn,
              strings.cipherColumn,
              strings.countColumn,
              strings.shareColumn,
              strings.englishColumn,
              strings.englishShareColumn,
            ]}
            rows={rows.map((row, rank) => [
              fmt(rank + 1),
              row.cipher ?? "—",
              fmt(row.count),
              percent(row.share),
              row.english,
              percent(row.englishShare),
            ])}
          />
        </div>
      </details>
    </div>
  );
}

function GuessGrid({
  counts,
  mapping,
  onChange,
  strings,
}: {
  counts: readonly number[];
  mapping: Mapping;
  onChange: (next: Mapping) => void;
  strings: SubstitutionStrings;
}) {
  const conflicts = mappingConflicts(mapping);
  return (
    <div className="grid grid-cols-7 gap-2 sm:grid-cols-9 md:grid-cols-13">
      {[...ALPHABET].map((letter, index) => {
        const guess = mapping[index];
        const clash = guess !== "" && conflicts.has(guess);
        return (
          <div
            className={`flex flex-col items-center gap-1 ${counts[index] === 0 ? "opacity-40" : ""}`}
            key={letter}
          >
            <label
              className="font-mono font-semibold text-foreground text-sm"
              htmlFor={`sub-guess-${letter}`}
            >
              {letter}
            </label>
            <input
              aria-label={t(strings.guessFor, { letter })}
              autoComplete="off"
              className={`h-9 w-9 rounded-md border bg-card text-center font-mono text-foreground lowercase focus:outline-none focus:ring-2 focus:ring-primary ${clash ? "border-destructive" : "border-border"}`}
              id={`sub-guess-${letter}`}
              onChange={(event) =>
                onChange(setGuess(mapping, letter, event.target.value))
              }
              onFocus={(event) => event.target.select()}
              spellCheck={false}
              value={guess.toLowerCase()}
            />
          </div>
        );
      })}
    </div>
  );
}

function WordHints({
  text,
  mapping,
  onApply,
  strings,
}: {
  text: string;
  mapping: Mapping;
  onApply: (next: Mapping) => void;
  strings: SubstitutionStrings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const hints = shortWordHints(text, mapping).slice(0, HINT_LIMIT);
  if (hints.length === 0) {
    return <Notice>{strings.noShortWords}</Notice>;
  }
  return (
    <ul className="divide-y divide-border">
      {hints.map((hint) => (
        <li
          className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2"
          key={hint.word}
        >
          <span className="w-12 font-mono font-semibold text-foreground">
            {hint.word}
          </span>
          <span className="w-20 text-muted-foreground text-xs">
            {tPlural(strings.wordCount, locale, hint.count, {
              count: fmt(hint.count),
            })}
          </span>
          {hint.candidates.length === 0 ? (
            <span className={HINT}>{strings.noCandidates}</span>
          ) : (
            hint.candidates.slice(0, CANDIDATE_LIMIT).map((candidate) => (
              <button
                aria-label={t(strings.tryWord, {
                  word: candidate,
                  cipher: hint.word,
                })}
                className={`${BUTTON_SMALL} font-mono lowercase`}
                key={candidate}
                onClick={() =>
                  onApply(applyWord(mapping, hint.word, candidate))
                }
                type="button"
              >
                {candidate}
              </button>
            ))
          )}
        </li>
      ))}
    </ul>
  );
}

function DoubleHints({
  text,
  strings,
}: {
  text: string;
  strings: SubstitutionStrings;
}) {
  const fmt = useToolNumber();
  const doubles = doubledLetters(text).slice(0, DOUBLE_LIMIT);
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.doublesHeading}</h3>
      {doubles.length === 0 ? (
        <p className={HINT}>{strings.noDoubles}</p>
      ) : (
        <p className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-foreground text-sm">
          {doubles.map((double) => (
            <span key={double.pair}>
              {t(strings.doubleCount, {
                pair: double.pair,
                count: fmt(double.count),
              })}
            </span>
          ))}
        </p>
      )}
      <p className={HINT}>{strings.doublesHint}</p>
    </div>
  );
}

/** The cipher text with each guess written under its letter, word by word. */
function SolvedView({ text, mapping }: { text: string; mapping: Mapping }) {
  const words = text.slice(0, VIEW_LIMIT).split(WORD_SPLIT);
  return (
    <div className="flex flex-wrap gap-y-3 rounded-lg border border-border bg-card p-4">
      {words.map((word, wordIndex) => {
        if (word.trim() === "") {
          return word === "" ? null : (
            // biome-ignore lint/suspicious/noArrayIndexKey: words are positional
            <span className="w-3" key={wordIndex} />
          );
        }
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: words are positional
          <span className="inline-flex" key={wordIndex}>
            {solveChars(word, mapping).map((char, index) => (
              <span
                className="inline-flex w-5 flex-col items-center font-mono"
                // biome-ignore lint/suspicious/noArrayIndexKey: letters are positional
                key={index}
              >
                <span className="text-muted-foreground text-xs">
                  {char.cipher}
                </span>
                <span className="font-semibold text-foreground">
                  {char.letter ? char.plain || "·" : char.cipher}
                </span>
              </span>
            ))}
          </span>
        );
      })}
    </div>
  );
}

function GuessControls({
  counts,
  mapping,
  onChange,
  strings,
}: {
  counts: readonly number[];
  mapping: Mapping;
  onChange: (next: Mapping) => void;
  strings: SubstitutionStrings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const present = counts.filter((count) => count > 0).length;
  const solved = counts.filter(
    (count, index) => count > 0 && mapping[index] !== ""
  ).length;
  const conflicts = [...mappingConflicts(mapping)];
  return (
    <section className={`${CARD} space-y-4`}>
      <h2 className={WIDGET_H2}>{strings.guessesHeading}</h2>
      <p className={HINT}>{strings.guessesHint}</p>
      <GuessGrid
        counts={counts}
        mapping={mapping}
        onChange={onChange}
        strings={strings}
      />
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-muted-foreground text-sm">
          {t(strings.guessed, { solved: fmt(solved), total: fmt(present) })}
        </span>
        <button
          className={BUTTON_SMALL}
          onClick={() => onChange(frequencyGuess(counts))}
          type="button"
        >
          {strings.fillFromFrequency}
        </button>
        <button
          className={BUTTON_SMALL}
          disabled={solved === 0}
          onClick={() => onChange(EMPTY_MAPPING)}
          type="button"
        >
          {strings.clearGuesses}
        </button>
      </div>
      {conflicts.length > 0 ? (
        <ErrorNotice>
          {tPlural(strings.conflict, locale, conflicts.length, {
            letters: conflicts.map((letter) => letter.toLowerCase()).join(", "),
          })}
        </ErrorNotice>
      ) : null}
    </section>
  );
}

export function SubstitutionSolver({
  strings,
}: {
  strings: SubstitutionStrings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState<string>(strings.cryptogramSample);
  const [mapping, setMapping] = useState<Mapping>(EMPTY_MAPPING);
  const counts = letterCounts(text);
  const total = counts.reduce((sum, count) => sum + count, 0);
  return (
    <>
      <section className="space-y-3">
        <TextAreaField
          id="sub-cryptogram"
          label={strings.cryptogram}
          onChange={setText}
          placeholder={strings.cryptogramPlaceholder}
          value={text}
        />
      </section>
      {total === 0 ? (
        <Notice>{strings.noLetters}</Notice>
      ) : (
        <>
          <section className={`${CARD} space-y-3`}>
            <h2 className={WIDGET_H2}>{strings.frequencyHeading}</h2>
            <p className="text-muted-foreground text-sm">
              {tPlural(strings.letterTotal, locale, total, {
                count: fmt(total),
              })}
            </p>
            <FrequencyChart counts={counts} strings={strings} />
          </section>
          <GuessControls
            counts={counts}
            mapping={mapping}
            onChange={setMapping}
            strings={strings}
          />
          <section className={`${CARD} space-y-4`}>
            <h2 className={WIDGET_H2}>{strings.hintsHeading}</h2>
            <p className={HINT}>{strings.hintsHint}</p>
            <WordHints
              mapping={mapping}
              onApply={setMapping}
              strings={strings}
              text={text}
            />
            <DoubleHints strings={strings} text={text} />
          </section>
          <section aria-live="polite" className="space-y-3">
            <h2 className={WIDGET_H2}>{strings.solutionHeading}</h2>
            <p className={HINT}>{strings.solutionHint}</p>
            <SolvedView mapping={mapping} text={text} />
            {text.length > VIEW_LIMIT ? (
              <p className={HINT}>
                {t(strings.viewLimit, { count: fmt(VIEW_LIMIT) })}
              </p>
            ) : null}
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copySolution}
              text={partialDecryption(text, mapping)}
            />
          </section>
        </>
      )}
    </>
  );
}
